import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Terminal, Plus, Trash2, Pencil, Megaphone, Sparkles, Volume2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import SoundPicker from "@/components/SoundPicker";

const EMPTY = { trigger: "", response: "", special_user: "", special_response: "", cost: 0, reward: 0, sound_url: "" };

function CommandDialog({ initial, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial || EMPTY);
  useEffect(() => { if (open) setForm(initial || EMPTY); }, [open, initial]);
  const save = async () => {
    if (!form.trigger.trim() || !form.response.trim()) { toast.error("Trigger and response required"); return; }
    await api.post("/commands", { ...form, cost: parseInt(form.cost) || 0, reward: parseInt(form.reward) || 0 });
    toast.success(`Command !${form.trigger.replace(/^!/, "")} saved`);
    setOpen(false); onSaved();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{initial ? "Edit" : "New"} Command</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label className="text-xs">Trigger (without !)</Label>
            <Input value={form.trigger} onChange={(e) => setForm({ ...form, trigger: e.target.value })} placeholder="hug" className="bg-secondary border-white/10 mt-1" data-testid="cmd-trigger" /></div>
          <div><Label className="text-xs">Default response <span className="text-muted-foreground">(use {"{user}"} for the caller)</span></Label>
            <Textarea value={form.response} onChange={(e) => setForm({ ...form, response: e.target.value })} placeholder="🤗 Sprinkles hugs {user}!" className="bg-secondary border-white/10 mt-1" data-testid="cmd-response" /></div>
          <div className="rounded-lg bg-secondary p-3 space-y-3">
            <p className="text-xs text-primary flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> Special outcome for one person</p>
            <div><Label className="text-xs">Special user</Label>
              <Input value={form.special_user} onChange={(e) => setForm({ ...form, special_user: e.target.value })} placeholder="streamer_mom" className="bg-background border-white/10 mt-1" data-testid="cmd-special-user" /></div>
            <div><Label className="text-xs">Special response</Label>
              <Textarea value={form.special_response} onChange={(e) => setForm({ ...form, special_response: e.target.value })} placeholder="Different outcome for that person…" className="bg-background border-white/10 mt-1" data-testid="cmd-special-response" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">🍪 Cost to use</Label>
              <Input type="number" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className="bg-secondary border-white/10 mt-1" data-testid="cmd-cost" /></div>
            <div><Label className="text-xs">🍪 Reward on use</Label>
              <Input type="number" value={form.reward} onChange={(e) => setForm({ ...form, reward: e.target.value })} className="bg-secondary border-white/10 mt-1" data-testid="cmd-reward" /></div>
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1"><Volume2 className="w-3 h-3 text-primary" /> Sound effect (plays on overlay)</Label>
            <div className="mt-1"><SoundPicker value={form.sound_url} onChange={(v) => setForm({ ...form, sound_url: v })} testid="cmd-sound" /></div>
          </div>
        </div>
        <DialogFooter><Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid="save-command-button">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Commands() {
  const [commands, setCommands] = useState([]);
  const [users, setUsers] = useState([]);
  const [soUser, setSoUser] = useState("pixel_knight");
  const [soResult, setSoResult] = useState("");
  const [soLoading, setSoLoading] = useState(false);

  const load = async () => {
    const [c, u] = await Promise.all([api.get("/commands"), api.get("/users")]);
    setCommands(c.data); setUsers(u.data);
  };
  useEffect(() => { load(); }, []);

  const del = async (t) => { await api.delete(`/commands/${t}`); load(); toast.success("Deleted"); };
  const walkon = async (name) => { const r = await api.post(`/walkon/${name}`); toast.success(r.data.message); };
  const shout = async () => {
    setSoLoading(true); setSoResult("");
    try { const r = await api.post(`/shoutout/${soUser}`); setSoResult(r.data.message); }
    catch { toast.error("Shoutout failed"); }
    finally { setSoLoading(false); }
  };

  const vips = users.filter((u) => u.is_vip);

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-800 font-heading">Commands</h1>
          <p className="text-muted-foreground mt-1 text-sm">Custom commands, per-person outcomes, walk-ons & AI shoutouts.</p>
        </div>
        <CommandDialog onSaved={load} trigger={<Button className="bg-primary hover:bg-primary/90 rounded-lg" data-testid="new-command-button"><Plus className="w-4 h-4 mr-1" /> New Command</Button>} />
      </header>

      {/* Custom commands */}
      <div className="mb-4 flex items-center justify-between bg-[#0a0a0a] border border-white/10 rounded-lg px-4 py-3">
        <div className="flex items-center gap-2 text-sm"><Terminal className="w-4 h-4 text-primary" /><span>Public commands & leaderboards page (share with viewers)</span></div>
        <a href="/commands" target="_blank" rel="noopener noreferrer" data-testid="open-public-commands">
          <Button size="sm" className="bg-primary hover:bg-primary/90"><ExternalLink className="w-4 h-4 mr-1" />Open /commands</Button>
        </a>
      </div>
      <div className="bg-[#0a0a0a] border border-white/10 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2 bg-black/40"><Terminal className="w-4 h-4 text-primary" /><h3 className="font-heading font-700 text-sm">Custom Commands</h3></div>
        <div className="divide-y divide-white/5" data-testid="commands-list">
          {commands.map((c) => (
            <div key={c.trigger} className="px-4 py-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <span className="text-primary font-mono font-semibold">!{c.trigger}</span>
                <p className="text-sm text-foreground/90 mt-0.5">{c.response}</p>
                {c.special_user && <p className="text-xs text-cookie mt-1">✨ Special for @{c.special_user}: {c.special_response}</p>}
                <div className="flex gap-2 mt-1 text-[11px] text-muted-foreground">
                  {c.cost > 0 && <span>cost {c.cost}🍪</span>}
                  {c.reward > 0 && <span>reward {c.reward}🍪</span>}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <CommandDialog initial={c} onSaved={load} trigger={<Button size="icon" variant="ghost" className="h-8 w-8 hover:text-primary" data-testid={`edit-command-${c.trigger}`}><Pencil className="w-4 h-4" /></Button>} />
                <Button size="icon" variant="ghost" className="h-8 w-8 hover:text-red-400" onClick={() => del(c.trigger)} data-testid={`delete-command-${c.trigger}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
          {commands.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">No custom commands yet.</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Shoutout */}
        <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-5">
          <div className="flex items-center gap-2 mb-3"><Megaphone className="w-4 h-4 text-primary" /><h3 className="font-heading font-700 text-sm">AI Shoutout</h3></div>
          <p className="text-xs text-muted-foreground mb-3">Sprinkles writes a kind compliment using their stream title & game, then links their page.</p>
          <div className="flex gap-2">
            <Input value={soUser} onChange={(e) => setSoUser(e.target.value)} list="so-users" className="bg-secondary border-white/10" data-testid="shoutout-user-input" />
            <datalist id="so-users">{users.map((u) => <option key={u.id} value={u.username} />)}</datalist>
            <Button onClick={shout} disabled={soLoading} className="bg-primary hover:bg-primary/90" data-testid="shoutout-button">{soLoading ? "…" : "Shout"}</Button>
          </div>
          {soResult && <div className="mt-3 p-3 rounded-lg bg-primary/10 border border-primary/30 text-sm animate-fade-up" data-testid="shoutout-result">{soResult}</div>}
        </div>

        {/* Walk-ons */}
        <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-5">
          <div className="flex items-center gap-2 mb-3"><Sparkles className="w-4 h-4 text-cookie" /><h3 className="font-heading font-700 text-sm">Walk-ons (VIPs)</h3></div>
          <p className="text-xs text-muted-foreground mb-3">Trigger an entrance announcement. Edit messages in the Economy tab.</p>
          <div className="space-y-2" data-testid="walkon-list">
            {vips.map((u) => (
              <div key={u.id} className="flex items-center justify-between gap-2 p-2 rounded-lg bg-secondary">
                <span className="text-sm truncate">@{u.username}</span>
                <Button size="sm" onClick={() => walkon(u.username)} className="bg-cookie text-black hover:bg-cookie/90 h-7" data-testid={`walkon-${u.username}`}>Trigger</Button>
              </div>
            ))}
            {vips.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No VIPs yet. Mark someone VIP in Economy.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
